import { Button, Grid } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { FieldConfig, FormConfig } from "./types/types";
// import { useFormStyles } from "./form.styles";
import { InputMUI } from "./MUI/InputMUI";
import { ShowIf } from "./ShowIf";
import React from "react";

/**
 * Reactive form that allows us to load an initial configuration, indicating the necessary field validations
 * and operations to create a new object or edit an existing one.
 * @param props FormConfig
 * @returns
 */
export const Form = ({
  loading,
  hasReset,
  initialValues,
  fields,
  mode,
  autofocus,
  onSubmit,
  onCancel,
  submitButtonText,
  cancelButtonText,
}: FormConfig) => {
  // const classes = useFormStyles();
  const {
    getValues,
    setValue,
    clearErrors,
    watch,
    control,
    handleSubmit,
    formState: { errors, isValid },
    setError
  } = useForm({
    mode,
  });

  /**
   * useCallback hook that loads the values of the initial payload when desired
   */
  const setInitialValues = useCallback(() => {
    Object.keys(initialValues ?? []).forEach((key) => {
      setValue(key, initialValues[key]);
    });
  }, [initialValues, setValue]);

  // Hook that controls the initial data load
  useEffect(() => {
    if (loading) return;

    setInitialValues();
  }, [loading, setInitialValues, setValue]);

  /**
   * List of fields that have the property computed
   */
  const computedFields = Object.keys(fields).filter((key) => {
    return !!fields[key].computed;
  });

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {

      const currentField = Object.keys(fields).find((key) => {
        return name === key;
      })
      if(currentField && fields[currentField]) {
        const watching = fields[currentField].watch
        watching?.forEach((item) => {
          if(item) {
            const rules = fields[item].rules
            const validation = rules.validate(value[item], value)
            setError(item, {type: "value", message: validation})
          }
        })
      }

      computedFields.forEach((key) => {
        const field = fields[key] as FieldConfig;
        if (field.type === "dropdown") return;

        const computedValue = field.computed(getValues());

        const oldValue = getValues(key);
        if (oldValue !== computedValue) setValue(key, computedValue);
      });
    });
    return () => subscription.unsubscribe();
  }, [computedFields, fields, getValues, setValue, watch, setError]);

  /**
   * Method that resets the initial values of the payload, clearing possible errors
   */
  const handleReset = () => {
    setInitialValues();
    clearErrors();
  };

  /**
   * Section responsible for displaying the list of fields
   */
  const formInputs = Object.keys(fields).map((fieldName, index) => {
    const { rules, defaultValue, xs, md, onError } = fields[fieldName];
    return (
      <Grid item xs={xs ?? 12} md={md ?? 12 }key={index} >
        {/* <div key={fieldName + "-contained"}> */}
          <Controller
            name={fieldName}
            control={control}
            rules={{
              ...rules,
              validate: (currentValue) => {
                return rules && typeof rules.validate === "function" ? rules.validate(currentValue, getValues()) : rules;
              },
            }}
            defaultValue={defaultValue ?? ""}
            render={({ field }) => (
              <InputMUI
                name={fieldName}
                loading={loading}
                value={field.value}
                onChange={field.onChange}
                watchingProperties={watch}
                getValues={getValues}
                onError={onError}
                autofocus={autofocus}
                error={errors[fieldName]?.message}
                {...fields[fieldName]}
              />
            )}
          />
        {/* </div> */}
      </Grid>
    );
  });

  return (
      <form
        onSubmit={handleSubmit(onSubmit)}
      >
        <Grid container spacing={1}>
          {formInputs}

        <div
          style={{
            justifyContent: !!hasReset || !!onCancel ? "space-between" : "center",
            display: "flex",
            flexDirection: "row-reverse",
            marginTop: "32px",
            width: "100%",
          }}
        >
          <div>
            <Button type="submit" disabled={loading || !isValid} variant="contained" data-testid="submit">
              {submitButtonText || "Crear"}
            </Button>
          </div>
          <ShowIf condition={!!hasReset}>
            <div>
              <Button disabled={loading} variant="text" onClick={handleReset} data-testid="reset">
                {submitButtonText || "Resetear"}
              </Button>
            </div>
          </ShowIf>
          <ShowIf condition={!!onCancel}>
            <div>
              <Button variant="text" onClick={() => onCancel!()} disabled={loading} data-testid="cancel">
                {cancelButtonText || "Cancelar"}
              </Button>
            </div>
          </ShowIf>
        </div>
        </Grid>
      </form>
  );
};
